import React, { useEffect, useState } from "react";
import { Star, MessageSquareText } from "lucide-react";
import api from "../api";
import { useLang } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { useToast } from "../hooks/use-toast";

function StarPicker({ value, onChange, readOnly = false, size = "w-5 h-5" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange(n)}
          className={readOnly ? "cursor-default" : "cursor-pointer"}
        >
          <Star
            className={`${size} ${(hover || value) >= n ? "text-brand-400" : "text-muted-foreground/40"}`}
            fill={(hover || value) >= n ? "currentColor" : "none"}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * `headline` is the book's own rating — the figure the catalogue and the card
 * already show. The panel leads with it so the page stops disagreeing with
 * itself: before this, the hero read "4.8 (158 ratings)" and the block directly
 * below it read "— / 0 ratings" off the review table alone.
 *
 * The breakdown underneath stays strictly what listeners wrote, and says so.
 * Those are two different measurements and the panel labels them as such rather
 * than blending one into the other.
 */
export default function Reviews({ bookId, owned, onAggregate, headline }) {
  const { t } = useLang();
  const { toast } = useToast();
  const [data, setData] = useState({ aggregate: { count: 0, distribution: {} }, reviews: [] });
  const [form, setForm] = useState({ story_rating: 0, performance_rating: 0, title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/books/${bookId}/reviews`).then((r) => {
      setData(r.data);
      if (onAggregate) onAggregate(r.data.aggregate);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.story_rating || !form.performance_rating) {
      toast({ title: "Please rate both story and performance", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/books/${bookId}/reviews`, form);
      toast({ title: "Thanks for your review!" });
      setForm({ story_rating: 0, performance_rating: 0, title: "", body: "" });
      load();
    } catch (e) {
      toast({ title: "Could not submit review", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const agg = data.aggregate;
  const total = agg.count || 0;

  // The headline falls back to the written reviews when the book carries no
  // rating of its own, so a title with only real reviews still reads correctly.
  const score = headline?.rating || agg.avg_overall || 0;
  const scoreCount = headline?.ratingCount || total;

  return (
    <div className="mt-14">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquareText className="w-5 h-5 text-brand-400" />
        <h2 className="font-display text-2xl font-semibold">{t("reviews")}</h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-10">
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card/50 p-6">
          <div className="font-display text-5xl font-semibold brand-text">{score ? score.toFixed(1) : "0.0"}</div>
          <StarPicker value={Math.round(score)} readOnly />
          <p className="text-xs text-muted-foreground mt-2">{scoreCount} {t("ratings")}</p>
        </div>
        <div className="md:col-span-2 space-y-2 flex flex-col justify-center">
          <p className="text-xs font-medium text-muted-foreground mb-1">
            {total > 0
              ? `Breakdown of ${total} written ${total === 1 ? "review" : "reviews"}`
              : "No written reviews yet"}
          </p>
          {[5, 4, 3, 2, 1].map((n) => {
            const c = agg.distribution?.[String(n)] || 0;
            const pct = total ? (c / total) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-10 flex items-center gap-0.5">{n}<Star className="w-3 h-3 text-brand-400" fill="currentColor" /></span>
                <Progress value={pct} className="h-2 flex-1" />
                <span className="text-xs text-muted-foreground w-8 text-right tabular-nums">{c}</span>
              </div>
            );
          })}
          {total > 0 && (
            <div className="flex gap-8 mt-3 text-sm">
              <span className="text-muted-foreground">{t("story_rating")}: <span className="text-foreground font-medium">{agg.avg_story}</span></span>
              <span className="text-muted-foreground">{t("performance_rating")}: <span className="text-foreground font-medium">{agg.avg_performance}</span></span>
            </div>
          )}
        </div>
      </div>

      {/* write review */}
      {owned ? (
        <div className="rounded-2xl border border-border bg-card/50 p-6 mb-8">
          <h3 className="font-semibold mb-4">{t("write_review")}</h3>
          <div className="flex flex-wrap gap-8 mb-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">{t("story_rating")}</p>
              <StarPicker value={form.story_rating} onChange={(v) => setForm({ ...form, story_rating: v })} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">{t("performance_rating")}</p>
              <StarPicker value={form.performance_rating} onChange={(v) => setForm({ ...form, performance_rating: v })} />
            </div>
          </div>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t("review_title")}
            className="w-full h-11 px-4 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 mb-3"
          />
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={t("review_body")}
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 mb-4"
          />
          <Button onClick={submit} disabled={submitting} className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full">
            {t("submit_review")}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mb-8">{t("review_own_hint")}</p>
      )}

      {/* review list */}
      <div className="space-y-5">
        {data.reviews.length === 0 && <p className="text-sm text-muted-foreground">{t("no_reviews")}</p>}
        {data.reviews.map((r) => (
          <div key={r.id} className="border-b border-border/60 pb-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-brand-950 text-sm font-bold">
                {r.user_name?.charAt(0).toUpperCase()}
              </span>
              <div>
                <p className="text-sm font-semibold">{r.user_name}</p>
                <StarPicker value={Math.round(r.overall)} readOnly size="w-3.5 h-3.5" />
              </div>
            </div>
            {r.title && <p className="font-medium text-sm">{r.title}</p>}
            {r.body && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.body}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
